import React, { useState, useEffect } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { misAPI } from '../lib/api';
import { formatCurrency, getStatusClass } from '../lib/utils';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  FileText,
  Loader2,
  RefreshCw,
  Download
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#6B7280'];
const STATUS_ORDER = ['LIVE', 'SENT FOR APPROVAL', 'APPROVED', 'REJECTED', 'PAID', 'CANCELLED'];

export default function MISReports() {
  const [summary, setSummary] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    fetchMISData();
  }, []);

  const fetchMISData = async () => {
    setLoading(true);
    try {
      const [summaryRes, claimsRes] = await Promise.all([
        misAPI.getSummary(),
        misAPI.getClaims(filters)
      ]);
      setSummary(summaryRes.data);
      setClaims(claimsRes.data);
    } catch (error) {
      toast.error('Failed to load MIS data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async () => {
    try {
      const response = await misAPI.getClaims(filters);
      setClaims(response.data);
    } catch (error) {
      toast.error('Failed to apply filters');
    }
  };

  const getChartData = () => {
    if (!summary?.status_counts) return [];
    return STATUS_ORDER.map((status, index) => ({
      name: status,
      count: summary.status_counts[status] || 0,
      fill: COLORS[index]
    }));
  };

  const getAmountChartData = () => {
    if (!summary?.amount_by_status) return [];
    return STATUS_ORDER.map((status, index) => ({
      name: status,
      amount: summary.amount_by_status[status]?.total_amount || 0,
      fill: COLORS[index]
    })).filter(item => item.amount > 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="mis-reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">MIS Reports</h1>
            <p className="text-muted-foreground">Overview of all advance claims</p>
          </div>
          <Button variant="outline" onClick={fetchMISData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card data-testid="total-claims-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Claims</p>
                  <p className="text-3xl font-bold">{summary?.total_claims || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="pending-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Pending Approval</p>
                  <p className="text-3xl font-bold">{summary?.status_counts?.['SENT FOR APPROVAL'] || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="approved-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Approved</p>
                  <p className="text-3xl font-bold">{summary?.status_counts?.APPROVED || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="paid-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Paid</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(summary?.amount_by_status?.PAID?.total_amount || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Claims by Status */}
          <Card data-testid="claims-by-status-chart">
            <CardHeader>
              <CardTitle>Claims by Status</CardTitle>
              <CardDescription>Distribution of claims across different statuses</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={12} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Amount by Status */}
          <Card data-testid="amount-by-status-chart">
            <CardHeader>
              <CardTitle>Amount by Status</CardTitle>
              <CardDescription>Total claim amounts by status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getAmountChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      dataKey="amount"
                    >
                      {getAmountChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filter Claims</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="w-48">
                <Label>Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value })}
                >
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                  data-testid="filter-from-date"
                />
              </div>
              <div className="w-48">
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                  data-testid="filter-to-date"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleFilterChange} data-testid="apply-filters-btn">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Claims Table */}
        <Card data-testid="claims-table">
          <CardHeader>
            <CardTitle>All Claims ({claims.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Claim No</th>
                    <th className="text-left py-3 px-4">Employee</th>
                    <th className="text-left py-3 px-4">Federation/RBO</th>
                    <th className="text-left py-3 px-4">Travel Date</th>
                    <th className="text-left py-3 px-4">Site</th>
                    <th className="text-right py-3 px-4">Amount</th>
                    <th className="text-center py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.slice(0, 50).map((claim) => (
                    <tr key={claim.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium">{claim.claim_number}</td>
                      <td className="py-3 px-4">
                        <div>
                          <p>{claim.user_name}</p>
                          <p className="text-xs text-muted-foreground">{claim.user_position}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p>{claim.federation_name}</p>
                          <p className="text-xs text-muted-foreground">{claim.rbo_name}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">{claim.date_of_travel}</td>
                      <td className="py-3 px-4">{claim.project_site_location}</td>
                      <td className="py-3 px-4 text-right font-medium">
                        {formatCurrency(claim.calculations?.advance_claim_amount)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={getStatusClass(claim.status)}>{claim.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {claims.length > 50 && (
                <p className="text-center text-muted-foreground py-4">
                  Showing 50 of {claims.length} claims
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
